/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Inject, Injectable, NgZone, OnDestroy} from '@angular/core';
import {StateService, UIRouter, UrlRouter} from '@uirouter/core';
import * as _ from 'lodash';

import {AuthService} from '../../components/auth/auth.service';
import {AUTH_EVENT} from '../../components/auth/auth-constants';
import {LocalDebugInfo} from '../../components/session/local-debug-info';
import {AdnLoginNavigationService} from '../../components/util/adn-login-navigation.service';
import {HOME_STATE} from '../home/home.states';
import {SIGN_IN_ROUTER_STATE} from './sign-in-views';
import {SignInModalService} from './sign-in-modal.service';
import {BrowserLocationService, WINDOW} from '../angular-platform-providers';
import {AuthHttpBufferService} from '../../components/auth/auth-http-buffer.service';
import {AppEventBusService} from '../common/events/app-event-bus.service';
import {AuthReturnNavigationService} from './auth-return-navigation.service';
import {BuildReloadService} from '../base/build-reload.service';

@Injectable({providedIn: 'root'})
export class SignInAuthListenerService implements OnDestroy {
  private modalOpen = false;
  private destroyListeners: Array<() => void> = [];

  constructor(
    private readonly ngZone: NgZone,
    private readonly signInModalService: SignInModalService,
    private readonly eventBus: AppEventBusService,
    private readonly stateService: StateService,
    private readonly uiRouter: UIRouter,
    @Inject(BrowserLocationService) private readonly location: any,
    private readonly $urlRouter: UrlRouter,
    @Inject(WINDOW) private readonly window: any,
    private readonly authHttpBuffer: AuthHttpBufferService,
    private readonly AuthService: AuthService,
    private readonly adnLoginNavigation: AdnLoginNavigationService,
    private readonly authReturnNavigation: AuthReturnNavigationService,
    private readonly buildReload: BuildReloadService
  ) {
    this.destroyListeners.push(
      this.uiRouter.transitionService.onSuccess({}, (transition) => {
        if (transition.to().name === 'sign-in') {
          this.leaveSignInWhenAlreadyAuthenticated();
        }
      }) as () => void
    );
    this.destroyListeners.push(
      this.eventBus.on(AUTH_EVENT.notAuthenticated, (_event: any, toState: any, toParams: any, rejectionData: any) => {
        this.ngZone.run(() => this.handleNotAuthenticated(toState, toParams, rejectionData));
      })
    );
    setTimeout(() => this.leaveSignInWhenAlreadyAuthenticated());
  }

  ngOnDestroy(): void {
    _.forEach(this.destroyListeners, (fn) => fn());
    this.destroyListeners = [];
  }

  private leaveSignInWhenAlreadyAuthenticated(): void {
    if (!this.stateService.is('sign-in') || this.stateService.params.explicitlySignedOut) {
      return;
    }
    if (this.AuthService.isAuthenticated() && !this.AuthService.getSession().isExpired()) {
      this.adnLoginNavigation.navigateAfterLogin(this.stateService, this.location, this.$urlRouter, {
        dest: this.stateService.params.dest
      });
    }
  }

  private handleNotAuthenticated(toState: any, toParams: any, rejectionData: any): void {
    if (this.modalOpen) {
      return;
    }
    if (this.authHttpBuffer.startupSessionRefreshInProgress
      || this.authHttpBuffer.authRefreshInProgress
      || this.authHttpBuffer.networkSwitchInProgress) {
      return;
    }
    if (this.AuthService.isValidSession() && !this.AuthService.getSession().isExpired()) {
      this.authHttpBuffer.loginConfirmed(this.AuthService.getSessionData());
      return;
    }

    LocalDebugInfo.obtain().setInfo('expired', new Date());

    const redirectToSignIn = () => {
      this.stateService.go('sign-in', {dest: this.location.url}, {inherit: false});
    };

    if (this.stateService.current.name === '') {
      setTimeout(() => {
        if (this.authHttpBuffer.startupSessionRefreshInProgress
          || this.authHttpBuffer.authRefreshInProgress
          || this.authHttpBuffer.networkSwitchInProgress) {
          return;
        }
        if (this.AuthService.isValidSession() && !this.AuthService.getSession().isExpired()) {
          this.authHttpBuffer.loginConfirmed(this.AuthService.getSessionData());
          return;
        }
        if (!this.AuthService.isAuthenticated()) {
          redirectToSignIn();
        }
      });
      return;
    }

    if (this.stateService.includes(HOME_STATE)
      || this.stateService.includes(SIGN_IN_ROUTER_STATE)
      || this.window.location.href.indexOf(SIGN_IN_ROUTER_STATE) > 0
      || this.window.location.href.indexOf('test-style') > 0) {
      redirectToSignIn();
      return;
    }

    if (this.stateService.current.data?.public) {
      return;
    }

    const capturedReturn = _.get(toState, 'name')
      ? {toState: toState, toParams: _.cloneDeep(toParams)}
      : this.authReturnNavigation.get();
    const returnToUrl = !capturedReturn ? this.location.url : null;

    this.modalOpen = true;
    this.signInModalService.open(rejectionData).subscribe({
      next: (session) => {
        if (!session) {
          this.authHttpBuffer.loginCancelled();
          return;
        }
        this.authHttpBuffer.loginConfirmed(session);
        const allPromises: any[] = [];
        if (capturedReturn && _.get(capturedReturn.toState, 'name')) {
          allPromises.push(this.adnLoginNavigation.navigateToStateOrHome(
            this.stateService, capturedReturn.toState, capturedReturn.toParams, {}
          ));
        } else if (returnToUrl) {
          allPromises.push(this.adnLoginNavigation.navigateAfterLogin(
            this.stateService, this.location, this.$urlRouter, {dest: returnToUrl}
          ));
        } else {
          allPromises.push(this.stateService.go('app.home', null, {inherit: false}));
        }
        Promise.all(allPromises).then(() => {
          this.authReturnNavigation.clear();
          this.buildReload.reloadIfNewerBuild();
        }, () => {
          this.adnLoginNavigation.navigateToStateOrHome(this.stateService, 'app.home', null, {}).then(() => {
            this.authReturnNavigation.clear();
            this.buildReload.reloadIfNewerBuild();
          });
        });
      },
      complete: () => {
        this.modalOpen = false;
      },
      error: () => {
        this.modalOpen = false;
      }
    });
  }

}
